/**
 * IANA timezone list + membership validation.
 *
 * Framework-free (no `$app/environment`) so it runs unchanged in Node logic
 * tests and the browser. `Intl.supportedValuesOf('timeZone')` returns the ~418
 * canonical zones but omits "UTC" — which the backend accepts and which is the
 * safe fallback — so we union it in. If the runtime lacks the API we fall back
 * to a small common list.
 */

const FALLBACK_TIMEZONES = [
	'UTC',
	'Europe/London',
	'Europe/Warsaw',
	'Europe/Berlin',
	'Europe/Paris',
	'America/New_York',
	'America/Chicago',
	'America/Denver',
	'America/Los_Angeles',
	'America/Sao_Paulo',
	'Asia/Tokyo',
	'Asia/Shanghai',
	'Asia/Kolkata',
	'Australia/Sydney'
];

function loadSupportedTimezones(): string[] {
	try {
		if (typeof Intl.supportedValuesOf === 'function') {
			return Intl.supportedValuesOf('timeZone');
		}
	} catch {
		// Older runtime — fall through to the fallback list.
	}
	return FALLBACK_TIMEZONES;
}

/** All selectable IANA timezones — "UTC" guaranteed present, sorted, unique. */
export const timezones: readonly string[] = (() => {
	const unique = new Set<string>(['UTC', ...loadSupportedTimezones()]);
	return [...unique].sort((a, b) => a.localeCompare(b));
})();

const timezoneSet = new Set(timezones);

/** True if `tz` is a timezone we offer (and the backend will accept). */
export function isValidTimezone(tz: string): boolean {
	return timezoneSet.has(tz);
}
